import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Link from 'components/base/Link'
import Title from 'components/base/Title'
import IconGrid from 'components/base/Icon/Grid'
import { Theme as IconTheme } from 'components/base/Icon/Grid/Icon'
import SectionRule from 'components/base/SectionRule/styles'
import Img from 'components/base/Img'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import CtaQuote from 'components/blocks/CtaQuote'
import AskQuestion from 'components/blocks/AskQuestion'
import InsuranceTypeIcon, { Theme as InsuranceTypeIconTheme } from 'components/base/Icon/InsuranceTypeIcon'

import FamilyHome from 'assets/images/products/FamilyHome.svg'
import HomeType from 'assets/images/products/HomeType.svg'

const ProductsHomeownersInsurance: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Homeowners Insurance | Insurance Lounge'
      description='Keep your family and home safe with homeowners insurance that provides the coverage you need most. Insurance Lounge can help you find the right one!'
    />
    <PageHero theme={PageHeroTheme.home} cta={true} ctaTitle='Get a homeowners insurance quote now.'>
      Exceptional Homeowners Insurance
    </PageHero>
    <Content short={true} margined={true} center={true}>
      <InsuranceTypeIcon theme={InsuranceTypeIconTheme.home} colored={true} hasMargin={true} />
      <Paragraph theme={ParagraphTheme.introLarge}>
        Purchasing a home is one of the biggest investments you’ll make. Keep your family and home safe with homeowners insurance that provides the coverage you need most. Insurance Lounge compares rates from numerous providers across Oregon to find the best policy for your home.
      </Paragraph>
      <Title level={2}>What Does Homeowners Insurance Cover?</Title>
      <Paragraph>
        Signing up for a homeowners’ insurance policy can provide coverage for damage resulting from the following:
      </Paragraph>
      <IconGrid
        icons={[
          {
            theme: IconTheme.fire,
            title: '',
          },
          {
            theme: IconTheme.wind,
            title: '',
          },
          {
            theme: IconTheme.burstPipe,
            title: '',
          },
          {
            theme: IconTheme.burglary,
            title: '',
          },
          {
            theme: IconTheme.hvac,
            title: '',
          },
          {
            theme: IconTheme.fallingTree,
            title: '',
          },
          {
            theme: IconTheme.injury,
            title: '',
          },
        ]}
      />
    </Content>
    <Content short={true} margined={true} center={true}>
      <Img
        src={FamilyHome}
        src2x={FamilyHome}
        alt='Beaverton Store'
      />
    </Content>
    <Content short={true} margined={true} center={true}>
      <Title level={2}>What Affects Your Home Insurance Rates?</Title>
      <Paragraph>
        The materials your house was built with, its square footage, and the building’s age have a significant impact on home insurance premiums. Your neighborhood, past insurance history, and <Link to='/products'>bundling options</Link> could also affect your insurance rates. No matter what kind of home you have, homeowners insurance will help protect your property and what’s in it, including your family.
      </Paragraph>
    </Content>
    <CtaQuote
      trackingInfo={{
        button_position: 'middle',
        page_category: 'product_page_homeowners',
      }}
    />
    <Content margined={true} center={true} short={true}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={HomeType}
          src2x={undefined}
          style={{
            height: 200,
            width: 'auto',
          }}
          alt='Business Type'
        />
      </Content>
      <Title level={2}>Common Home Insurance Coverage Types</Title>
      <Paragraph>Most homeowners policies are &quot;package policies&ldquo; that cover property damage and liability injuries, depending on the coverage you purchase. The most common types of homeowners insurance are:</Paragraph>
      <Title level={3}>Dwelling</Title>
      <Paragraph>
        Dwelling coverage includes the cost of rebuilding or repairing your home after a fire or natural disaster. Dwelling coverage is usually included in your homeowners policy as a covered
        hazard. This means your house and connected structures, like a garage, should be protected.
      </Paragraph>
      <Title level={3}>Personal Property</Title>
      <Paragraph>
        Personal property is categorized as everything you own on the property that isn’t the home itself, like furniture, clothing, electronics, etc. Adding personal property coverage to your policy is encouraged to help protect or replace belongings after a fire or burglary. Having to replace all of your belongings out of pocket would be difficult, so personal property coverage is here to help.
      </Paragraph>
      <Title level={3}>Liability</Title>
      <Paragraph>
        Personal liability protects you from lawsuits related to injuries or property damage that you or your family, including pets, may cause to other people. Depending on your policy, liability coverage may pay for the costs of defending you in court and cover the settlement that’s awarded in a lawsuit.
      </Paragraph>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Paragraph>
        Insurance Lounge is committed to making it easy for homeowners <Link to='/locations/storefronts'>in Oregon</Link> and other states to find the perfect policy. With a <Link to='/about-us/our-team'>team</Link> that’s focused on technology and innovation, we’re creating new ways to make shopping for insurance more transparent.
        We’ll work with you to understand your needs and find the best deal on the market. Contact us at 1-800-211-8660 or <Link to='/quote/new'>fill out our form</Link> to get a free quote today.
      </Paragraph>
    </Content>
    <SectionRule />
    <AskQuestion title='We’re Here, When You Need Us'>
      Reach out and an Insurance Concierge<sup>®</sup> will get in touch!
    </AskQuestion>
  </div>
)

export default ProductsHomeownersInsurance
